import { AlertTriangle, Info } from "lucide-react";

import { VCM_CONFIG_CHECK, type ExecutionMode } from "@/api/readyz";

interface ExecutionModeNoticeProps {
  /** The deployment's execution mode, from `useExecutionMode()`. */
  mode: ExecutionMode;
  /** True while the readiness report is still in flight. Suppresses the
   *  `unknown` line so a notice doesn't flash on every page load. */
  isLoading?: boolean;
}

/**
 * States, on the submission path, whether this deployment can really compute
 * — BEFORE anything is submitted (flowstack#1174).
 *
 * The failure this exists for: a stub-mode deployment renders the submit page
 * exactly as a compute-capable one does — the full 15-node DAG, the molecule
 * picker, an enabled Submit button — and the first signal that nothing will be
 * computed is a `failed_contract` run after the fact. Two people spent a
 * working session reasoning about that deployment and reached the wrong
 * conclusion in opposite directions; the fact was on the wire the whole time,
 * in `/api/readyz`'s `vcm_config` check.
 *
 * Three rules this component is built around:
 *
 * 1. **The Submit button is not disabled and this does not touch it.**
 *    Submitting against the stub is legitimate — it exercises input
 *    validation, the compiler, the DAG and the run lifecycle. The goal is an
 *    informed submission, not a blocked one.
 * 2. **Stub is a working path, not a broken deployment.** It has a specific,
 *    documented ending (`failed_contract`), and saying "unconfigured" or "no
 *    compute" about it would be the same wrong claim in the other direction.
 * 3. **We never fill in what we do not know.** `unknown` says so; it does not
 *    default to reassurance.
 *
 * Rendered by both submit routes (Flow and Tile): the fact is a property of
 * the deployment, so it sits at the top of the page, where it is read on
 * arrival rather than after the inputs have been wired.
 */
export function ExecutionModeNotice({
  mode,
  isLoading = false,
}: ExecutionModeNoticeProps): JSX.Element | null {
  // Atlas-configured: the page already means what it appears to mean.
  if (mode === "atlas") return null;

  if (mode === "unknown") {
    // No report, an unusable one, or a report from a server too old to carry
    // the check — the reader collapses all of them here. Say that, quietly: an
    // absence of knowledge is not an absence of compute, and a transient readyz
    // blip should not read like an outage.
    if (isLoading) return null;
    return (
      <p className="text-xs text-muted-foreground" data-testid="execution-mode-unknown">
        This deployment&rsquo;s execution mode is unknown &mdash;{" "}
        <span className="font-mono">/api/readyz</span> gave no usable execution-mode report, so this
        page cannot say whether submitting here computes a result.
      </p>
    );
  }

  const stub = mode === "stub";
  // Stub is by design, so it reads as information (neutral chrome). `failed`
  // is a deployment that does not hold together, so it reads as a warning.
  // Both are existing lifecycle tokens; no new colour is introduced.
  const tone = stub
    ? "border-state-validating-border bg-state-validating-bg/40"
    : "border-state-failed-validation-border bg-state-failed-validation-bg/50";
  const iconTone = stub ? "text-state-validating" : "text-state-failed-validation-fg";
  const Icon = stub ? Info : AlertTriangle;
  const headingId = "execution-mode-heading";
  // The field name comes from the constant the reader uses, not a second copy
  // of the string, so a rename cannot leave this line quoting a field that no
  // longer exists (Rupert PR-1175 r0).
  const reportedAs = `${VCM_CONFIG_CHECK}: ${stub ? "skipped" : "failed"}`;

  return (
    <div
      role="note"
      aria-labelledby={headingId}
      data-testid={stub ? "execution-mode-stub" : "execution-mode-unusable"}
      className={`rounded-md border border-l-4 p-4 ${tone}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconTone}`} aria-hidden />
        <div className="space-y-2 text-xs text-foreground">
          <h2 id={headingId} className="text-sm font-semibold">
            {stub
              ? "Submissions here run against the stub, not real compute."
              : "This deployment cannot dispatch compute as configured."}
          </h2>
          {stub ? (
            <p>
              Submitting still does something worth doing: it validates your inputs, compiles the
              submission, and the run appears in <span className="font-medium">Runs</span> and moves
              through the lifecycle like any other. It will not compute a result. The stub produces
              none of the outputs each Tile declares, so every run submitted here ends{" "}
              <span className="font-mono">failed_contract</span> instead of succeeding.
            </p>
          ) : (
            <p>
              The API reports its compute configuration as failed. Submitting still exercises
              validation and the parameter form, but no run submitted here will compute a result.
              What becomes of the submission depends on which part of the configuration does not
              hold, and this page does not predict it.
            </p>
          )}
          <p className="text-muted-foreground">
            Reported by <span className="font-mono">GET /api/readyz</span> as{" "}
            <span className="font-mono">{reportedAs}</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
